import { useMemo, useState } from "react"
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar, faCircleInfo, faPlus, faTrashCan } from "@fortawesome/pro-light-svg-icons"

import { useApiQuery } from "../../../core/api/useApiQuery"
import {
    CompanyAdminWorkgroupsQuery,
    WorkgroupsForCompanyResponse,
    fetchWorkgroupsForCompany,
} from "../../../modules/company/admin/organization/workgroups/workgroupsApi"
import { selectEffectiveCompanyId } from "../../../modules/context/contextSelectors"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { useAppSelector } from "../../../modules/store/storeHooks"

import { StandardButton } from "../buttons/StandardButton"
import { StandardDialog } from "../dialogs/StandardDialog"

type WorkgroupMembership = Readonly<{
    id: number
    name: string
}>

// Minimal shape this panel reads/writes on the surrounding Formik form; both the office and company
// user forms satisfy it via their `workgroups` field.
type WorkgroupsFormValues = {
    workgroups: ReadonlyArray<WorkgroupMembership>
}

const useStyles = makeStyles()((theme) => ({
    selectedList: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
    },
    selectedCard: {
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
        padding: ".75rem 1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".5rem",
    },
    trashButton: {
        color: "rgba(0, 0, 0, 0.56)",
        fontSize: "1rem",
    },
    empty: {
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
        padding: ".5rem 0",
    },
    addButtonRow: {
        display: "flex",
        justifyContent: "flex-end",
    },
    addButton: {
        "&.MuiButton-root": {
            color: "#2196F3",
            borderColor: "#2196F3",
            padding: "0 .75rem",
            borderRadius: ".5rem",
            "& .MuiButton-startIcon svg": {
                width: ".8125rem",
                height: ".75rem",
            },
        },
    },
    dialogLoading: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "6rem",
    },
    dialogList: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
        padding: "0 1.5rem 1.5rem",
    },
    pickerItem: {
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
        width: "100%",
        padding: ".75rem 1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".5rem",
        cursor: "pointer",
        transition: "background-color .15s ease, border-color .15s ease",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[50],
            borderColor: "#2196F3",
        },
        "&:focus-visible": {
            outline: `2px solid #2196F3`,
            outlineOffset: "2px",
        },
    },
    pickerHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: ".5rem",
    },
    pickerName: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[900],
    },
    infoIcon: {
        display: "inline-flex",
        color: theme.palette.tailwind.slate[400],
        fontSize: ".875rem",
        cursor: "pointer",
    },
    pickerCalendarRow: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        color: theme.palette.tailwind.slate[500],
    },
    pickerCalendarIcon: {
        fontSize: ".75rem",
    },
    pickerDescription: {
        color: theme.palette.tailwind.slate[500],
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    dialogEmpty: {
        textAlign: "center",
        color: theme.palette.tailwind.slate[400],
        padding: "1rem 2.5rem",
    },
}))

const selectWorkgroups = (data: WorkgroupsForCompanyResponse) => data?.workgroups || []

export function WorkgroupsMembershipPanel() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const { values, setFieldValue } = useFormikContext<WorkgroupsFormValues>()

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)

    const currentWorkgroups = values.workgroups

    const workgroupsResult = useApiQuery(fetchWorkgroupsForCompany, {
        queryName: CompanyAdminWorkgroupsQuery.LIST,
        dependencies: {
            companyId: companyId ?? 0,
        },
        // Fetch when the picker opens, and also when the user already has workgroups so the selected cards can be
        // enriched with each workgroup's calendar email and description (the form value only carries id + name).
        enabled: Boolean(companyId) && (isAddDialogOpen || currentWorkgroups.length > 0),
        select: selectWorkgroups,
    })

    const workgroupById = useMemo(
        () => new Map((workgroupsResult.data ?? []).map((workgroup) => [workgroup.id, workgroup])),
        [workgroupsResult.data],
    )

    const availableWorkgroups = (workgroupsResult.data ?? []).filter(
        (workgroup) => !currentWorkgroups.some((current) => current.id === workgroup.id),
    )

    const handleAdd = (workgroup: WorkgroupMembership) => {
        setFieldValue("workgroups", [...currentWorkgroups, { id: workgroup.id, name: workgroup.name }])
        setIsAddDialogOpen(false)
    }

    const handleRemove = (workgroupId: number) => {
        setFieldValue(
            "workgroups",
            currentWorkgroups.filter((workgroup) => workgroup.id !== workgroupId),
        )
    }

    return (
        <>
            {currentWorkgroups.length > 0 ? (
                <Box className={classes.selectedList}>
                    {currentWorkgroups.map((workgroup) => {
                        const details = workgroupById.get(workgroup.id)

                        return (
                            <Box key={workgroup.id} className={classes.selectedCard}>
                                <Box className={classes.pickerHeader}>
                                    <Typography variant="body2" className={classes.pickerName}>
                                        {workgroup.name}
                                    </Typography>
                                    <IconButton
                                        aria-label={l10n.getString("company-user-workgroups-panel-remove")}
                                        onClick={() => handleRemove(workgroup.id)}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} className={classes.trashButton} />
                                    </IconButton>
                                </Box>
                                {details?.calendarEmailAddress && (
                                    <Box className={classes.pickerCalendarRow}>
                                        <FontAwesomeIcon icon={faCalendar} className={classes.pickerCalendarIcon} />
                                        <Typography variant="caption">{details.calendarEmailAddress}</Typography>
                                    </Box>
                                )}
                                {details?.description && (
                                    <Typography variant="body2" className={classes.pickerDescription}>
                                        {details.description}
                                    </Typography>
                                )}
                            </Box>
                        )
                    })}
                </Box>
            ) : (
                <LocalizedStrict id="company-user-workgroups-panel-empty">
                    <Typography variant="body2" className={classes.empty}>
                        This user is not a member of any workgroups yet.
                    </Typography>
                </LocalizedStrict>
            )}

            <Box className={classes.addButtonRow}>
                <LocalizedStrict id="company-user-workgroups-panel-add-button">
                    <StandardButton
                        variant="outlined"
                        fullWidth={false}
                        className={classes.addButton}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        Add
                    </StandardButton>
                </LocalizedStrict>
            </Box>

            {isAddDialogOpen && (
                <StandardDialog
                    dialogTitle={l10n.getString("company-user-workgroups-panel-add-dialog-title")}
                    open={isAddDialogOpen}
                    onClose={() => setIsAddDialogOpen(false)}
                    maxWidthProp="sm"
                    scroll="body"
                >
                    {workgroupsResult.isInitialLoading ? (
                        <Box className={classes.dialogLoading}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : availableWorkgroups.length > 0 ? (
                        <Box className={classes.dialogList}>
                            {availableWorkgroups.map((workgroup) => (
                                <Box
                                    key={workgroup.id}
                                    role="button"
                                    tabIndex={0}
                                    className={classes.pickerItem}
                                    onClick={() => handleAdd(workgroup)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault()
                                            handleAdd(workgroup)
                                        }
                                    }}
                                >
                                    <Box className={classes.pickerHeader}>
                                        <Typography variant="body2" className={classes.pickerName}>
                                            {workgroup.name}
                                        </Typography>
                                        {workgroup.description && (
                                            <Tooltip title={workgroup.description} arrow>
                                                <Box component="span" className={classes.infoIcon}>
                                                    <FontAwesomeIcon icon={faCircleInfo} />
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    {workgroup.calendarEmailAddress && (
                                        <Box className={classes.pickerCalendarRow}>
                                            <FontAwesomeIcon icon={faCalendar} className={classes.pickerCalendarIcon} />
                                            <Typography variant="caption">{workgroup.calendarEmailAddress}</Typography>
                                        </Box>
                                    )}
                                    {workgroup.description && (
                                        <Typography variant="body2" className={classes.pickerDescription}>
                                            {workgroup.description}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <LocalizedStrict id="company-user-workgroups-panel-add-dialog-empty">
                            <Typography variant="body1" className={classes.dialogEmpty}>
                                There are no more workgroups to add.
                            </Typography>
                        </LocalizedStrict>
                    )}
                </StandardDialog>
            )}
        </>
    )
}
